import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { CircleAlert, Info, Loader2, TriangleAlert } from 'lucide-react'
import { Button } from '@/src/renderer/components/ui/Button'
import { PasswordInput } from '@/src/renderer/components/ui/PasswordInput'
import { useBackupBridge, type PasswordStatus } from '@/src/renderer/lib/backup/backupBridge'
import { SettingsSubpage } from '../../primitives'
import './BackupViews.scss'

/** So lang muss es mindestens sein — der Hauptprozess prüft dasselbe. */
export const MIN_BACKUP_PASSWORD_LENGTH = 6

interface BackupPasswordViewProps {
  /** `setup`: zum ersten Mal; `change`: es gibt schon eins — ältere Backups behalten ihres. */
  mode: 'setup' | 'change'
  onBack: () => void
  onSaved: (status: PasswordStatus) => void
}

/**
 * **Backups › Passwort** (#158, Entscheidung 17): Passwort zweimal, dazu die Warnung, dass ein Backup
 * ohne das Passwort nicht mehr aufgeht. Gespeichert wird im Schlüsselbund dieses Geräts; angezeigt wird
 * es nie wieder. Beim Ändern sagt die Seite, dass ältere Backups ihr bisheriges Passwort behalten.
 */
export function BackupPasswordView({ mode, onBack, onSaved }: BackupPasswordViewProps) {
  const { t } = useTranslation()
  const bridge = useBackupBridge()
  const [password, setPassword] = useState('')
  const [repeat, setRepeat] = useState('')
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)

  // Hinweise erst, wenn man ein Feld verlässt — mitten im Tippen sagen sie nur, was noch nicht fertig ist.
  const [shown, setShown] = useState({ tooShort: false, mismatch: false })
  const valid = password.length >= MIN_BACKUP_PASSWORD_LENGTH && repeat === password
  const check = () => setShown({
    tooShort: password.length > 0 && password.length < MIN_BACKUP_PASSWORD_LENGTH,
    mismatch: repeat.length > 0 && repeat !== password,
  })
  const tooShort = shown.tooShort
  const mismatch = shown.mismatch

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!valid || busy) return
    setBusy(true)
    setFailed(false)
    try {
      onSaved(await bridge.setPassword(password))
    } catch (error) {
      console.error('[Backup] Passwort nicht gespeichert:', error)
      setFailed(true)
      setBusy(false)
    }
  }

  return (
    <SettingsSubpage
      pageTitle={t('settings.subNav.backups')}
      title={t(mode === 'change' ? 'preferences.backups.password.changeTitle' : 'preferences.backups.password.setupTitle')}
      onBack={onBack}
      locked={busy}
    >
      <p className="backup-views__intro">{t('preferences.backups.password.intro')}</p>
      <p className="backup-views__result backup-views__result--warning">
        <TriangleAlert size={16} aria-hidden="true" />
        {t('preferences.backups.password.warning')}
      </p>
      {mode === 'change' && (
        <p className="backup-views__result">
          <Info size={16} aria-hidden="true" />
          {t('preferences.backups.password.changedNote')}
        </p>
      )}
      <form className="backup-views__password" onSubmit={event => void onSubmit(event)}>
        <label className="backup-views__label" htmlFor="backup-password-new">{t('preferences.backups.password.password')}</label>
        <PasswordInput
          id="backup-password-new"
          value={password}
          onChange={event => { setPassword(event.target.value); setShown({ tooShort: false, mismatch: false }) }}
          onBlur={check}
          autoComplete="new-password"
          autoFocus
          error={tooShort}
        />
        <label className="backup-views__label" htmlFor="backup-password-repeat">{t('preferences.backups.password.repeat')}</label>
        <PasswordInput
          id="backup-password-repeat"
          value={repeat}
          onChange={event => { setRepeat(event.target.value); setShown(prev => ({ ...prev, mismatch: false })) }}
          onBlur={check}
          autoComplete="new-password"
          error={mismatch}
        />
        <div role="status" aria-live="polite" className="backup-views__checks">
          {tooShort && <p className="backup-views__hint-line">{t('preferences.backups.password.tooShort', { count: MIN_BACKUP_PASSWORD_LENGTH })}</p>}
          {mismatch && <p className="backup-views__hint-line">{t('preferences.backups.password.mismatch')}</p>}
        </div>
        <div className="backup-views__actions">
          <Button
            type="submit"
            variant="primary"
            disabled={!valid || busy}
            icon={busy ? <Loader2 size={16} className="backup-views__spinner" aria-hidden="true" /> : undefined}
          >
            {t('preferences.backups.password.save')}
          </Button>
        </div>
        {failed && (
          <p className="backup-views__result backup-views__result--failed" role="alert">
            <CircleAlert size={16} aria-hidden="true" />
            {t('preferences.backups.password.saveFailed')}
          </p>
        )}
      </form>
    </SettingsSubpage>
  )
}
